import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import {
  ArrowRight, Download, Check, X, ChevronDown,
  Github, Zap, GitBranch, Box, Hash, Layers, Bell,
  Crown, PlayCircle, Sparkles,
} from "lucide-react";

const BRAND = "#5A90F5";

function useEntryHref() {
  const { user } = useAuth();
  return user ? "/app" : "/register";
}

// ─── Animated number counter ──────────────────────────────────────────────

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

// ─── Nav ─────────────────────────────────────────────────────────────────────

function Nav() {
  const entryHref = useEntryHref();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#080808]/95 backdrop-blur-2xl border-b border-white/[0.05]" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="shrink-0"><Logo size={18} /></Link>
        <nav className="hidden md:flex items-center gap-10 text-[12px] tracking-wide uppercase text-[#555]">
          {[["#features","Features"],["#pricing","Pricing"],["#faq","FAQ"]].map(([href,label]) => (
            <a key={href} href={href} className="hover:text-[#EDEDED] transition-colors duration-200">{label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-[12px] uppercase tracking-wide text-[#555] hover:text-[#EDEDED] transition-colors duration-200">Sign In</Link>
          <Link to={entryHref}
            className="group relative px-5 py-2 text-[12px] uppercase tracking-wide font-medium rounded-full text-white overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(90,144,245,0.4)]"
            style={{ background: `linear-gradient(135deg, #5A90F5, #3B6FD4)` }}>
            Get Started
          </Link>
        </div>
      </div>
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero() {
  const entryHref = useEntryHref();
  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#080808]">
      {/* Layered background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Noise texture */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: "200px 200px",
        }} />
        {/* Large glow top-center */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] opacity-30"
          style={{ background: `radial-gradient(ellipse, ${BRAND} 0%, transparent 65%)`, filter: "blur(80px)" }} />
        {/* Bottom left accent */}
        <div className="absolute bottom-0 left-0 w-[500px] h-[400px] opacity-10"
          style={{ background: `radial-gradient(ellipse, #A855F7 0%, transparent 70%)`, filter: "blur(60px)" }} />
        {/* Grid lines */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
          backgroundSize: "80px 80px",
        }} />
        {/* Vignette */}
        <div className="absolute inset-0" style={{
          background: "radial-gradient(ellipse at center, transparent 40%, #080808 100%)"
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24 flex flex-col items-center text-center">
        {/* Pill badge */}
        <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-[11px] tracking-[0.15em] uppercase text-[#666] mb-12 backdrop-blur-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-[#5A90F5] animate-pulse" />
          Public Beta — Free for early sign-ups
        </div>

        {/* Headline */}
        <h1 className="text-[clamp(3rem,9vw,7rem)] font-light tracking-[-0.03em] leading-[0.9] text-[#EDEDED] max-w-5xl mb-8">
          The calendar that{" "}
          <span className="relative inline-block">
            <span className="relative z-10 font-semibold italic" style={{
              background: `linear-gradient(135deg, #7EB0FF 0%, ${BRAND} 40%, #3B6FD4 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>thinks</span>
          </span>
          {" "}with you.
        </h1>

        <p className="text-[#555] text-lg md:text-xl max-w-xl leading-relaxed mb-12">
          Crescendo is a node-based workflow canvas living inside your calendar.
          Wire triggers into actions, branch on conditions, wrap routines into sequences —
          without ever leaving your week.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center gap-3 mb-6">
          <Link to={entryHref}
            className="group inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-white text-sm font-medium tracking-wide transition-all duration-300 hover:shadow-[0_0_50px_rgba(90,144,245,0.5)] hover:scale-[1.02]"
            style={{ background: `linear-gradient(135deg, #6BA3FF, #4070D4)` }}>
            Start Free
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
          <Link to="/downloads"
            className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full border border-white/[0.08] bg-white/[0.02] text-[#888] text-sm tracking-wide hover:text-[#EDEDED] hover:border-white/[0.15] hover:bg-white/[0.05] transition-all duration-200">
            <Download className="w-4 h-4" />
            Download App
          </Link>
        </div>
        <p className="text-[11px] tracking-wider uppercase text-[#333]">No credit card · Free forever plan available</p>

        {/* App screenshot */}
        <div className="mt-20 w-full max-w-5xl relative">
          {/* Glow behind window */}
          <div className="absolute -inset-4 pointer-events-none"
            style={{ background: `radial-gradient(ellipse at 50% 0%, ${BRAND}20, transparent 60%)`, filter: "blur(40px)" }} />
          {/* Window */}
          <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_40px_120px_rgba(0,0,0,0.9)]"
            style={{ background: "#0C0C0C" }}>
            {/* Chrome bar */}
            <div className="h-10 flex items-center gap-2 px-4 border-b border-white/[0.05]" style={{ background: "#0E0E0E" }}>
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ background: "#FF5F57" }} />
                <span className="w-3 h-3 rounded-full" style={{ background: "#FEBC2E" }} />
                <span className="w-3 h-3 rounded-full" style={{ background: "#28C840" }} />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-4 py-1 rounded-md text-[11px] text-[#333] border border-white/[0.04]" style={{ background: "#111" }}>
                  crescendo-switchboard.com
                </div>
              </div>
            </div>
            <img src="/hero-app-screenshot.png" alt="Crescendo workspace"
              className="w-full block"
              style={{ aspectRatio: "16/9", objectFit: "cover", objectPosition: "top" }} />
          </div>
          {/* Bottom fade */}
          <div className="absolute bottom-0 inset-x-0 h-32 pointer-events-none rounded-b-2xl"
            style={{ background: "linear-gradient(to top, #080808, transparent)" }} />
        </div>
      </div>
    </section>
  );
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────

function StatsBar() {
  return (
    <div className="border-y border-white/[0.05] bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
        {[
          { value: "4", unit: "Plan tiers", note: "from free to enterprise" },
          { value: "∞", unit: "Nodes per canvas", note: "no artificial caps" },
          { value: "50+", unit: "Automation types", note: "triggers, actions, conditions" },
          { value: "7×", unit: "Faster planning", note: "vs. manual scheduling" },
        ].map((s, i) => (
          <div key={i} className="flex flex-col">
            <span className="text-4xl md:text-5xl font-light tracking-tight text-[#EDEDED] mb-1"
              style={{ fontVariantNumeric: "tabular-nums" }}>{s.value}</span>
            <span className="text-sm font-medium text-[#555] mb-0.5">{s.unit}</span>
            <span className="text-[11px] text-[#333]">{s.note}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Features ─────────────────────────────────────────────────────────────────

const FEATURES = [
  { icon: Zap,       color: "#FF8A00", title: "Trigger → Action",     body: "Mark a task done and your calendar shifts the next block automatically. Real cause-and-effect across your whole week." },
  { icon: GitBranch, color: "#A855F7", title: "Branching Conditions", body: "True and false paths. Weekday or weekend? Done by 5pm? Build real logic into your plan — not just lists." },
  { icon: Box,       color: "#14B8A6", title: "Sequence Containers",  body: "Wrap many nodes into one and run them sequentially or in parallel. Morning routine, launch checklist — one unit." },
  { icon: Hash,      color: "#00C781", title: "Classes & Tags",        body: "Color-code across workspaces. Filter the canvas to just the work that matters right now." },
  { icon: Layers,    color: BRAND,     title: "Nested Workspaces",     body: "Folders, drag-and-drop reorg, class groupings, templates. Crescendo stays calm no matter how much you pile on." },
  { icon: Bell,      color: "#F43F5E", title: "Notes On Every Node",  body: "Write directly on a node. Pin it to a date. The note travels with the event — never lost in a second app." },
];

function Features() {
  return (
    <section id="features" className="bg-[#080808] scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#333] mb-5">// Features</p>
            <h2 className="text-4xl md:text-6xl font-light tracking-tight text-[#EDEDED] leading-[0.95]">
              Everything your calendar<br />was <em className="not-italic font-semibold" style={{ color: BRAND }}>missing.</em>
            </h2>
          </div>
          <p className="text-sm text-[#444] max-w-xs leading-relaxed md:text-right">
            Nodes that fire each other, sequences that group entire routines, conditions that branch your week.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          {/* Large card — first feature */}
          <div className="md:col-span-7 group relative rounded-2xl border border-white/[0.05] bg-[#0C0C0C] p-10 overflow-hidden hover:border-white/[0.1] transition-all duration-300">
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
              style={{ background: `radial-gradient(circle at 30% 50%, ${FEATURES[0].color}08, transparent 60%)` }} />
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-8 border border-white/[0.06]"
              style={{ background: `${FEATURES[0].color}12` }}>
              <Zap size={18} color={FEATURES[0].color} />
            </div>
            <h3 className="text-2xl font-light text-[#EDEDED] mb-3">{FEATURES[0].title}</h3>
            <p className="text-sm text-[#555] leading-relaxed max-w-sm">{FEATURES[0].body}</p>
          </div>

          {/* Tall card */}
          <div className="md:col-span-5 group relative rounded-2xl border border-white/[0.05] bg-[#0C0C0C] p-8 overflow-hidden hover:border-white/[0.1] transition-all duration-300">
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
              style={{ background: `radial-gradient(circle at 70% 30%, ${FEATURES[1].color}08, transparent 60%)` }} />
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-8 border border-white/[0.06]"
              style={{ background: `${FEATURES[1].color}12` }}>
              <GitBranch size={18} color={FEATURES[1].color} />
            </div>
            <h3 className="text-2xl font-light text-[#EDEDED] mb-3">{FEATURES[1].title}</h3>
            <p className="text-sm text-[#555] leading-relaxed">{FEATURES[1].body}</p>
          </div>

          {/* Three smaller cards */}
          {FEATURES.slice(2).map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="md:col-span-4 group relative rounded-2xl border border-white/[0.05] bg-[#0C0C0C] p-7 overflow-hidden hover:border-white/[0.1] transition-all duration-300">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: `radial-gradient(circle at 50% 50%, ${f.color}06, transparent 60%)` }} />
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-6 border border-white/[0.06]"
                  style={{ background: `${f.color}10` }}>
                  <Icon size={16} color={f.color} />
                </div>
                <h3 className="text-lg font-light text-[#EDEDED] mb-2">{f.title}</h3>
                <p className="text-xs text-[#444] leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── How It Works ─────────────────────────────────────────────────────────────

function HowItWorks() {
  return (
    <section className="bg-[#080808] border-t border-white/[0.04]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-28">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#333] mb-5">// How It Works</p>
        <h2 className="text-4xl md:text-6xl font-light tracking-tight text-[#EDEDED] mb-16 leading-[0.95]">See it in two minutes.</h2>
        <div className="relative aspect-video rounded-2xl border border-white/[0.06] bg-[#0C0C0C] overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: `radial-gradient(ellipse at 50% 30%, ${BRAND}12, transparent 55%)` }} />
          <div className="relative flex flex-col items-center gap-5">
            <div className="w-20 h-20 rounded-full border border-white/[0.08] bg-[#141414] flex items-center justify-center cursor-pointer hover:border-[#5A90F5]/40 hover:bg-[#0E1525] transition-all duration-300 group">
              <PlayCircle className="w-9 h-9 transition-colors duration-200 group-hover:text-[#7EB0FF]" style={{ color: BRAND }} />
            </div>
            <div className="text-center">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#333] mb-1">Promo video coming soon</p>
              <p className="text-[11px] text-[#222]">A 2-minute tour through Crescendo's workflow canvas.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

const PLANS = [
  { id: "free",     name: "Free",     price: 0,  unit: "forever",       cta: "Start Free",    ctaHref: "/register",                                     highlight: false },
  { id: "standard", name: "Standard", price: 8,  unit: "/ mo",          cta: "Get Standard",  ctaHref: "https://buy.stripe.com/00w3cx8TGh1hbiR7SJ8so03", highlight: false },
  { id: "pro",      name: "Pro",      price: 16, unit: "/ mo",          cta: "Go Pro",        ctaHref: "https://buy.stripe.com/7sY4gB9XK6mD9aJgpf8so04", highlight: true, tag: "Most Popular" },
  { id: "business", name: "Business", price: 20, unit: "/ seat / mo",   cta: "Talk To Us",    ctaHref: "mailto:hello@crescendo-switchboard.com",           highlight: false },
];

const LIMIT_ROWS = [
  { key: "workspaces",         label: "Workspaces",           fmt: v => v === -1 ? "Unlimited" : String(v) },
  { key: "templates",          label: "Templates",            fmt: v => v === -1 ? "Unlimited" : String(v) },
  { key: "documents",          label: "Documents",            fmt: v => v === -1 ? "Unlimited" : String(v) },
  { key: "tabs",               label: "Document tabs",        fmt: v => v === -1 ? "Unlimited" : String(v) },
  { key: "key_pairs",          label: "Key-pair variables",   fmt: v => v === -1 ? "Unlimited" : String(v) },
  { key: "version_days",       label: "Version history",      fmt: v => v === -1 ? "All-time" : `${v} days` },
  { key: "resources_per_node", label: "Resources per node",   fmt: v => v === -1 ? "Unlimited" : String(v) },
  { key: "time_zones",         label: "Time zone views",      fmt: v => v === -1 ? "Unlimited" : String(v) },
];

const FEATURE_ROWS = [
  { key: "triggers_actions",  label: "Triggers & Actions" },
  { key: "secure_sharing",    label: "Secure Sharing" },
  { key: "day_tints",         label: "Day Tints" },
  { key: "spotlight_ai",      label: "Spotlight AI" },
  { key: "highlight_roster",  label: "Highlight Roster" },
  { key: "custom_fonts",      label: "Custom Fonts" },
  { key: "integrations",      label: "Slack & GitHub Integrations" },
  { key: "email_embedding",   label: "Email Embedding" },
  { key: "priority_email",    label: "Priority Email Support" },
  { key: "otp",               label: "Over-the-Phone Support" },
  { key: "team",              label: "Team Workspace" },
  { key: "free_pro_seats",    label: "Free Pro seat per team member" },
];

const PLAN_LIMITS = {
  free:     { workspaces: 2,  templates: 10,  documents: 10,  tabs: 5,   key_pairs: 10, version_days: 7,  resources_per_node: 1,  time_zones: 1  },
  standard: { workspaces: 10, templates: 50,  documents: 50,  tabs: 25,  key_pairs: 50, version_days: 30, resources_per_node: 5,  time_zones: 2  },
  pro:      { workspaces: 20, templates: 200, documents: 200, tabs: 100, key_pairs: -1, version_days: -1, resources_per_node: -1, time_zones: -1 },
  business: { workspaces: -1, templates: -1,  documents: -1,  tabs: -1,  key_pairs: -1, version_days: -1, resources_per_node: -1, time_zones: -1 },
};

const PLAN_FEATURES = {
  free:     { triggers_actions: false, secure_sharing: false, day_tints: false, spotlight_ai: false, highlight_roster: false, custom_fonts: false, otp: false, integrations: false, email_embedding: false, priority_email: false, team: false, free_pro_seats: false },
  standard: { triggers_actions: true,  secure_sharing: true,  day_tints: true,  spotlight_ai: true,  highlight_roster: false, custom_fonts: false, otp: false, integrations: false, email_embedding: false, priority_email: false, team: false, free_pro_seats: false },
  pro:      { triggers_actions: true,  secure_sharing: true,  day_tints: true,  spotlight_ai: true,  highlight_roster: true,  custom_fonts: true,  otp: false, integrations: true,  email_embedding: true,  priority_email: true,  team: false, free_pro_seats: false },
  business: { triggers_actions: true,  secure_sharing: true,  day_tints: true,  spotlight_ai: true,  highlight_roster: true,  custom_fonts: true,  otp: true,  integrations: true,  email_embedding: true,  priority_email: true,  team: true,  free_pro_seats: true  },
};

function TableCell({ value }) {
  if (typeof value === "boolean") {
    return value
      ? <div className="flex justify-center"><Check className="w-4 h-4" style={{ color: BRAND }} /></div>
      : <div className="flex justify-center"><X className="w-3.5 h-3.5 text-[#2A2A2A]" /></div>;
  }
  const special = value === "Unlimited" || value === "All-time";
  return (
    <div className="text-center">
      <span className={`text-sm ${special ? "font-medium" : "text-[#555]"}`}
        style={special ? { color: BRAND } : {}}>{value}</span>
    </div>
  );
}

function Pricing() {
  const entryHref = useEntryHref();
  return (
    <section id="pricing" className="bg-[#080808] border-t border-white/[0.04] scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-28">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#333] mb-5">// Pricing</p>
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-4 gap-6">
          <h2 className="text-4xl md:text-6xl font-light tracking-tight text-[#EDEDED] leading-[0.95]">
            Simple,<br />transparent pricing.
          </h2>
          <p className="text-sm text-[#444] max-w-xs leading-relaxed md:text-right">
            Every limit. Every feature. No surprises.
            Pro is free during the public beta.
          </p>
        </div>

        {/* Plan header cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-14 mb-1">
          {PLANS.map(p => (
            <div key={p.id}
              className={`relative rounded-2xl border p-6 flex flex-col transition-all duration-300 ${
                p.highlight
                  ? "border-[#5A90F5]/30 bg-[#0B101E]"
                  : "border-white/[0.05] bg-[#0C0C0C]"
              }`}>
              {p.tag && (
                <div className="absolute -top-3 left-5 px-3 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-medium text-white"
                  style={{ background: `linear-gradient(135deg, #6BA3FF, #4070D4)` }}>{p.tag}</div>
              )}
              {p.id === "pro" && <Crown className="w-4 h-4 text-[#FFCC00] mb-3" />}
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#444] mb-2">{p.name}</p>
              <div className="flex items-baseline gap-1.5 mb-5">
                <span className="text-3xl font-light text-[#EDEDED] tracking-tight">${p.price}</span>
                <span className="text-[11px] text-[#333]">{p.unit}</span>
              </div>
              <a href={p.id === "free" ? entryHref : p.ctaHref}
                className={`py-2.5 rounded-xl text-[12px] uppercase tracking-wide font-medium text-center transition-all duration-200 mt-auto ${
                  p.highlight
                    ? "text-white hover:opacity-90"
                    : "bg-white/[0.04] text-[#888] border border-white/[0.06] hover:bg-white/[0.07] hover:text-[#EDEDED]"
                }`}
                style={p.highlight ? { background: `linear-gradient(135deg, #6BA3FF, #4070D4)` } : {}}>
                {p.cta}
              </a>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <div className="rounded-2xl border border-white/[0.05] overflow-hidden">
          {/* Limits */}
          <div className="px-6 py-3 bg-[#0D0D0D] border-b border-white/[0.04]">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#333]">Limits</p>
          </div>
          {LIMIT_ROWS.map((row, i) => (
            <div key={row.key}
              className={`grid grid-cols-5 items-center px-6 py-3.5 border-b border-white/[0.03] ${i % 2 === 0 ? "bg-[#090909]" : "bg-[#0B0B0B]"}`}>
              <div className="col-span-1 text-sm text-[#444]">{row.label}</div>
              {PLANS.map(p => (
                <TableCell key={p.id} value={row.fmt(PLAN_LIMITS[p.id][row.key])} />
              ))}
            </div>
          ))}

          {/* Features */}
          <div className="px-6 py-3 bg-[#0D0D0D] border-b border-white/[0.04] border-t border-white/[0.05]">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#333]">Features</p>
          </div>
          {FEATURE_ROWS.map((row, i) => (
            <div key={row.key}
              className={`grid grid-cols-5 items-center px-6 py-3.5 ${i % 2 === 0 ? "bg-[#090909]" : "bg-[#0B0B0B]"} ${i < FEATURE_ROWS.length - 1 ? "border-b border-white/[0.03]" : ""}`}>
              <div className="col-span-1 text-sm text-[#444]">{row.label}</div>
              {PLANS.map(p => (
                <TableCell key={p.id} value={PLAN_FEATURES[p.id][row.key]} />
              ))}
            </div>
          ))}
        </div>

        <p className="text-[11px] text-[#2A2A2A] text-center mt-6 tracking-wide">
          Business plan requires minimum 2 seats · each seat includes a free personal Pro account
        </p>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────

const FAQS = [
  { q: "Is Crescendo a calendar or a workflow tool?", a: "Both. The grid is a real time-bound calendar. The canvas is a visual workflow with nodes and edges. Events live on both at once — your routines don't exist in two apps." },
  { q: "Do I need to be technical to use triggers and conditions?", a: "No. Triggers are just 'when X happens, do Y.' Conditions are 'if it's a weekday, go down this branch.' If you've ever sorted email into folders, you already think this way." },
  { q: "What's the difference between Standard and Pro?", a: "Standard unlocks triggers, Spotlight AI, and secure sharing — everything most people need. Pro adds custom fonts, full integrations, email embedding, priority support, and unlimited everything." },
  { q: "Does it work offline? Is my data private?", a: "All data lives in your account and can be exported at any time. Offline support and a local-first desktop app are on the roadmap." },
  { q: "How does pricing work during beta?", a: "Pro is free for everyone during the public beta. When billing turns on, early sign-ups keep Pro through a transition window — no surprise charges." },
  { q: "What is Spotlight AI?", a: "Spotlight is the command palette. On Standard and above, it understands natural language — type 'meeting with Alex at 3pm tomorrow' and it creates the event, positioned correctly on your canvas." },
];

function Faq() {
  const [open, setOpen] = useState(null);
  return (
    <section id="faq" className="bg-[#080808] border-t border-white/[0.04] scroll-mt-16">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-28">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#333] mb-5">// FAQ</p>
        <h2 className="text-4xl md:text-6xl font-light tracking-tight text-[#EDEDED] mb-16 leading-[0.95]">
          Questions people<br />actually ask.
        </h2>
        <div>
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="border-t border-white/[0.05]">
                <button onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group">
                  <span className={`text-base leading-relaxed transition-colors duration-200 ${isOpen ? "text-[#EDEDED]" : "text-[#666] group-hover:text-[#EDEDED]"}`}>{f.q}</span>
                  <div className={`w-6 h-6 rounded-full border flex-shrink-0 flex items-center justify-center transition-all duration-200 ${isOpen ? "border-[#5A90F5]/40 bg-[#5A90F5]/10" : "border-white/[0.08]"}`}>
                    <ChevronDown className={`w-3 h-3 transition-all duration-300 ${isOpen ? "rotate-180 text-[#5A90F5]" : "text-[#333]"}`} />
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-60 pb-6" : "max-h-0"}`}>
                  <p className="text-sm text-[#555] leading-relaxed">{f.a}</p>
                </div>
              </div>
            );
          })}
          <div className="border-t border-white/[0.05]" />
        </div>
      </div>
    </section>
  );
}

// ─── CTA ──────────────────────────────────────────────────────────────────────

function CtaBanner() {
  const entryHref = useEntryHref();
  return (
    <section className="bg-[#080808] border-t border-white/[0.04]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="relative rounded-3xl overflow-hidden border border-white/[0.06]" style={{ background: "#0C0C0C" }}>
          {/* Background effects */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px]"
              style={{ background: `radial-gradient(ellipse, ${BRAND}15, transparent 70%)`, filter: "blur(40px)" }} />
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }} />
            <div className="absolute inset-0" style={{
              background: "radial-gradient(ellipse at center, transparent 50%, #0C0C0C 100%)"
            }} />
          </div>
          <div className="relative px-10 py-20 md:py-28 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] text-[11px] tracking-[0.15em] uppercase text-[#555] mb-8">
              <Sparkles className="w-3 h-3" style={{ color: BRAND }} />
              Free during beta
            </div>
            <h2 className="text-4xl md:text-6xl font-light tracking-tight text-[#EDEDED] mb-5 leading-[0.95]">
              Start building your week.
            </h2>
            <p className="text-sm text-[#444] mb-10 max-w-sm mx-auto leading-relaxed">
              Free forever. No credit card. Upgrade only when you need more.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to={entryHref}
                className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-white text-sm font-medium tracking-wide transition-all duration-300 hover:shadow-[0_0_50px_rgba(90,144,245,0.4)] hover:scale-[1.02]"
                style={{ background: `linear-gradient(135deg, #6BA3FF, #4070D4)` }}>
                Get Started Free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/downloads"
                className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full border border-white/[0.08] text-[#666] text-sm hover:text-[#EDEDED] hover:border-white/[0.14] transition-all duration-200">
                <Download className="w-4 h-4" /> Download App
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="border-t border-white/[0.04] bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid md:grid-cols-5 gap-12">
        <div className="md:col-span-2">
          <Logo size={16} />
          <p className="mt-5 text-sm text-[#333] max-w-xs leading-relaxed">
            A node-based workflow tool for people who design their days.
          </p>
        </div>
        {[
          {
            title: "Product",
            links: [["#features","Features"],["#pricing","Pricing"],["#faq","FAQ"],["/downloads","Download"],["/register","Sign Up"]],
          },
          {
            title: "Company",
            links: [["mailto:hello@crescendo-switchboard.com","Contact"],["#","Privacy"],["#","Terms"]],
          },
        ].map(col => (
          <div key={col.title}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#2A2A2A] mb-5">{col.title}</p>
            <ul className="space-y-3">
              {col.links.map(([href, label]) => (
                <li key={label}>
                  {href.startsWith("/")
                    ? <Link to={href} className="text-sm text-[#444] hover:text-[#EDEDED] transition-colors duration-200">{label}</Link>
                    : <a href={href} className="text-sm text-[#444] hover:text-[#EDEDED] transition-colors duration-200">{label}</a>
                  }
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/[0.03]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <span className="text-[11px] text-[#222] tracking-wide">© {new Date().getFullYear()} Crescendo</span>
          <a href="https://github.com/AbsolutelyMallishing/Crescendo"
            className="text-[11px] text-[#222] hover:text-[#555] transition-colors duration-200 inline-flex items-center gap-1.5 tracking-wide">
            <Github className="w-3.5 h-3.5" /> GitHub
          </a>
        </div>
      </div>
    </footer>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#080808] text-[#EDEDED]">
      <Nav />
      <Hero />
      <StatsBar />
      <Features />
      <HowItWorks />
      <Pricing />
      <Faq />
      <CtaBanner />
      <Footer />
    </div>
  );
}
